// Profile: the signed-in user's self-service page. Shows the read-only account
// fields and lets the user EDIT their own display name (PATCH /users/me) and
// CHANGE their password (POST /users/me/password). Linked from the userbar menu.
//
// What a member's profile is NOT: it used to accumulate everything a member could
// still touch after BF28 admin-gated the rest — their mcp-access tokens, their
// default assistant engine, their network memberships. Three answers to three
// different questions on one page, two of which a member cannot act on.
//   · mcp-access tokens moved back to the /tokens tab, which is now a member tab
//     (the card was always caller-scoped) — one place per thing, not two.
//   · "My default engine" is GONE (UX8). It was a control whose meaning depended
//     on operator configuration, on a page about who you are — and the engine
//     roster it picked from lives on the Engines page, which is where an operator
//     is when they care.
//   · "Network memberships" moved to the Networks page (UX9), where the networks
//     are. A membership is a fact about a network, not about a face.

import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { api, ApiError } from '../api/client';
import { Card, Loading, Badge, StatusBadge, Avatar } from '../components/ui';
import { DetailRow } from '../components/Drawer';

// Which cards the profile shows for a role. Pure (exported for tests): a member
// gets their account and their password, nothing else.
// Which cards the profile shows. UX8/UX9 left exactly two, for every role: who you
// are, and how you sign in. Still a function (and still exported) because it is what
// the test asserts against — a card creeping back in should fail something.
export function profileCards(_isAdmin: boolean): string[] {
  return ['account', 'password'];
}

// Client-side validation for the change-password form. Returns an error message
// to show, or null when the input is acceptable. Pure (exported for tests).
export function validatePasswordChange(
  current: string,
  next: string,
  confirm: string,
): string | null {
  if (!current) return 'Enter your current password.';
  if (next.length < 8) return 'New password must be at least 8 characters.';
  if (next !== confirm) return 'New passwords do not match.';
  return null;
}

function errMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : String(e);
}

export function Profile() {
  const { me, refresh } = useAuth();

  if (!me) return <Loading what="Loading profile…" />;
  const { user, org } = me;

  return (
    <div className="view">
      <div className="view-head">
        <h1>Profile</h1>
      </div>

      <Card title="Account">
        <div className="profile-head">
          <Avatar name={user.name || user.email} colorKey={user.id || user.name || user.email} size={48} />
          <div>
            <div className="profile-name">{user.name || user.email}</div>
            <div className="muted small">{user.email}</div>
          </div>
        </div>
        <DisplayNameForm
          current={user.name ?? ''}
          onSaved={() => {
            // Refresh /auth/me so the userbar (and this page) pick up the new name.
            void refresh();
          }}
        />
        <DetailRow label="Email">{user.email}</DetailRow>
        <DetailRow label="User id"><code className="mono small">{user.id}</code></DetailRow>
        <DetailRow label="Organization">{org?.name ?? <code className="mono small">{user.org_id}</code>}</DetailRow>
        <DetailRow label="Role"><Badge tone={user.role === 'admin' ? 'info' : 'muted'}>{user.role}</Badge></DetailRow>
        <DetailRow label="Status"><StatusBadge status={user.status} /></DetailRow>
        {user.created_at && <DetailRow label="Created">{new Date(user.created_at).toLocaleString()}</DetailRow>}
      </Card>

      <Card title="Change password">
        <ChangePasswordForm />
      </Card>
    </div>
  );
}

function DisplayNameForm({ current, onSaved }: { current: string; onSaved: () => void }) {
  const [name, setName] = useState(current);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  // Keep the field in sync if the underlying user changes (e.g. after a refresh).
  useEffect(() => {
    setName(current);
  }, [current]);

  const dirty = name.trim() !== current.trim();

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setOk(false);
    try {
      await api.updateProfile({ display_name: name.trim() });
      setOk(true);
      onSaved();
    } catch (ex) {
      setErr(errMessage(ex));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="profile-form">
      <label className="field">Display name
        <input
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setOk(false);
          }}
          placeholder="Your name"
          autoComplete="name"
        />
      </label>
      {err && <div className="form-error">{err}</div>}
      {ok && !dirty && <div className="form-ok">Display name saved.</div>}
      <div className="profile-actions">
        <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !dirty}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

function ChangePasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setOk(false);
    const invalid = validatePasswordChange(current, next, confirm);
    if (invalid) {
      setErr(invalid);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.changePassword({ current_password: current, new_password: next });
      setOk(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (ex) {
      // Surfaces the backend 400 reason (wrong current / too short / SSO-only).
      setErr(errMessage(ex));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="profile-form">
      <label className="field">Current password
        <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      </label>
      <label className="field">New password
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" placeholder="at least 8 characters" />
      </label>
      <label className="field">Confirm new password
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
      </label>
      {err && <div className="form-error">{err}</div>}
      {ok && <div className="form-ok">Password changed.</div>}
      <div className="profile-actions">
        <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>
          {busy ? 'Changing…' : 'Change password'}
        </button>
      </div>
    </form>
  );
}
